/* eslint-disable sonarjs/function-return-type */
import { TagSelectionField } from '@src/entities/Tag/ui/components/fields/TagSelection';
import { User } from '@src/entities/User/ui/components/User';
import { fieldValidator } from '@src/shared/includes/validation';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { NumberInput } from '@uhg-abyss/web/ui/NumberInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isEmpty, isNil, isUndefined, merge, orderBy } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import { NotFound } from '../NotFound';
import configuration from './includes/configuration.json';
import fieldConfiguration from './includes/fields.json';
import { Styles } from './includes/styles';

/**
 * Table
 *
 * Provides the user with a table to specify filters to search risk records by.
 *
 * @param append
 * @param assets
 * @param dataKey
 * @param fields
 * @param filterKey
 * @param form
 * @param handleReset
 * @param isDisabled
 * @param isLoading
 * @param permissions
 * @param remove
 * @returns {Element}
 * @constructor
 */
export function Table({
  append = () => {},
  assets = {},
  dataKey = '',
  fields = [],
  filterKey = 'filters',
  form = {
    formState: {
      errors: {},
    },
    getValues: () => {},
    resetField: () => {},
    setValue: () => {},
    validate: () => {},
  },
  handleReset = () => {},
  isDisabled = false,
  isLoading = false,
  remove = () => {},
  permissions = {
    add: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
    edit: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
    remove: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
  },
}) {
  const actionPaths = assets?.ListActionPaths?.data || [];
  const actionStatuses = assets?.ListActionStatuses?.data || [];
  const tags = assets?.ListTags?.data || [];

  const filters = form.getValues(filterKey);

  const { column: fieldColumn, condition: fieldCondition, value: fieldValue } = fieldConfiguration;

  /**
   * fieldValue.status.options
   *
   * This is a memoized value that is used to populate the status options for the select input.
   */
  fieldValue.status.options = useMemo(() => {
    return actionStatuses?.map((actionStatus) => {
      return {
        label: actionStatus?.codeDesc,
        value: actionStatus?.codeId,
      };
    });
  }, [actionStatuses]);

  /**
   * fieldValue.tags.options
   *
   * This is a memoized value that is used to populate the tags options for the select input.
   *
   * @type {*[]}
   */
  fieldValue.tags.options = useMemo(() => {
    const tagOptions = [];

    if (!isEmpty(tags?.tagsList)) {
      Object.keys(tags?.tagsList).forEach((categoryCode) => {
        const tagOption = {
          items: [],
          section: '',
        };
        tags?.tagsList[categoryCode].forEach((tag) => {
          if (isEmpty(tagOption.section)) {
            tagOption.section = tag?.categoryDesc;
          }
          const tagItem = {
            label: `${tag?.code} (${tag?.categoryCode})`,
            tags: [tag.categoryDesc, tag.description],
            value: tag?.tag,
          };
          if (!tagOption.items.includes(tagItem)) {
            tagOption.items.push(tagItem);
          }
        });

        if (!tagOptions.includes(tagOption)) {
          tagOptions.push(tagOption);
        }
      });
    }

    return tagOptions;
  }, [tags]);

  /**
   * fieldValue.actionPath.options
   *
   * This is a memoized value that is used to populate the tags options for the select input.
   *
   * @type {*[]}
   */
  fieldValue.actionPath.options = useMemo(() => {
    let actionPathOptions = [];

    if (!isEmpty(actionPaths?.content)) {
      actionPathOptions = [
        {
          items: actionPaths?.content
            ?.filter((actionPath) => {
              return actionPath?.manualAssociation === false;
            })
            .map((actionPath) => {
              return {
                label: `${actionPath?.name} (automatic)`,
                value: actionPath?.id,
              };
            }),
          section: 'Automatic',
        },
        {
          items: actionPaths?.content
            ?.filter((actionPath) => {
              return actionPath?.manualAssociation === true;
            })
            .map((actionPath) => {
              return {
                label: `${actionPath?.name} (manual)`,
                value: actionPath?.id,
              };
            }),
          section: 'Manual',
        },
      ];
    }

    return actionPathOptions;
  }, [actionPaths]);

  /**
   * fieldColumn.options
   *
   * Sort the field options by label.
   *
   * @type {Array}
   */
  fieldColumn.options = useMemo(() => {
    return orderBy(fieldColumn?.options, ['label'], ['asc']);
  }, [fieldColumn?.options]);

  /**
   * fieldCondition.options
   *
   * Sort the condition options by label.
   *
   * @type {Array}
   */
  fieldCondition.options = useMemo(() => {
    return orderBy(fieldCondition?.options, ['label'], ['asc']);
  }, [fieldCondition?.options]);

  /**
   * validate after setting initial row.
   */
  useEffect(() => {
    if ([filterKey]?.length === 1) {
      form.validate(
        `${filterKey}.0.column`,
        () => {},
        () => {}
      );
    }
  }, [filters]);

  /**
   * validate when fields changes
   */
  useEffect(() => {
    form.validate(
      `${filterKey}.0.column`,
      () => {},
      () => {}
    );
  }, [fields]);

  /**
   * validateField
   *
   * Validate specified form field(s).
   *
   * @param index
   * @param field
   */
  const validateField = (index, field) => {
    form.validate(
      `${filterKey}[${index}][${field}]`,
      () => {},
      () => {}
    );
  };

  /**
   * Validate all repeatable fields within a row when a value changes.
   */
  useEffect(() => {
    if (!isNil(filters) && !isEmpty(filters)) {
      filters.forEach((filter, index) => {
        validateField(index, 'column');
        if (!isEmpty(filter?.column) && isUndefined(form?.formState?.errors?.[filterKey]?.[index]?.column)) {
          validateField(index, 'condition');
          if (!isEmpty(filter?.condition) && isUndefined(form?.formState?.errors?.[filterKey]?.[index]?.condition)) {
            if (['CREATED_DATE', 'LAST_MODIFIED_DATE'].includes(filter?.column)) {
              form?.setValue(`${filterKey}[${index}][value]`, dayjs(filter?.value).format('MM/DD/YYYY'), {
                shouldDirty: true,
              });
            }

            if (filter?.column?.toLowerCase().includes('count')) {
              form?.setValue(`${filterKey}[${index}][value]`, String(filter?.value));
            }

            validateField(index, 'value');
          }
        }
      });
    }
  }, [filters, form?.formState?.errors]);

  /**
   * renderCellField
   *
   * Displays the field select input.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCellField = (args) => {
    const { row } = args;
    const { index } = row;

    return (
      <User.Capability
        alias="edit"
        attributes={permissions?.edit?.attributes}
        isAllowed={permissions?.edit?.isAllowed}
        resource={permissions?.edit?.resource}
      >
        <SelectInput
          {...fieldColumn}
          isDisabled={isDisabled}
          model={`${filterKey}[${index}][column]`}
          onChange={(columnValue) => {
            form?.setValue(`${filterKey}[${index}][condition]`, '');

            if (['ACTION_PATH_ID', 'TAGS'].includes(columnValue)) {
              form?.setValue(`${filterKey}[${index}][value]`, []);
            } else {
              form?.setValue(`${filterKey}[${index}][value]`, '');
            }

            validateField(index, 'column');
            validateField(index, 'condition');
          }}
          validators={{
            ...fieldColumn?.validators,
            ...{
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fieldColumn, value);
                },
              },
            },
          }}
        />
      </User.Capability>
    );
  };

  /**
   * renderCellCondition
   *
   * Displays the condition select input.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCellCondition = (args) => {
    const { row } = args;
    const { index } = row;

    const isFieldDisabled =
      !isUndefined(form?.formState?.errors?.[filterKey]?.[index]?.column) || isEmpty(row?.original?.column);

    return (
      <User.Capability
        alias="edit"
        attributes={permissions?.edit?.attributes}
        isAllowed={permissions?.edit?.isAllowed}
        resource={permissions?.edit?.resource}
      >
        <SelectInput
          {...fieldCondition}
          isDisabled={isDisabled || isFieldDisabled}
          model={`${filterKey}[${index}][condition]`}
          onChange={() => {
            validateField(index, 'condition');
            validateField(index, 'column');
            validateField(index, 'value');
          }}
          options={fieldCondition?.options.filter((option) => {
            if (row?.original?.column === 'ACTION_PATH_ID') {
              return ['ALL', 'ANY', 'NONE', 'NOTALL'].includes(option?.value);
            }

            if (row?.original?.column === 'ACTION_STATUS') {
              return ['EQ', 'NE'].includes(option?.value);
            }

            if (row?.original?.column === 'SPLIT_DATE') {
              return ['GT'].includes(option?.value);
            }

            if (row?.original?.column === 'TAGS') {
              return ['ALL', 'ANY', 'NONE', 'NOTALL', 'ONLY'].includes(option?.value);
            }

            if (row?.original?.column.toLowerCase().includes('count')) {
              return ['EQ', 'GT', 'GTE', 'LT', 'LTE', 'NE'].includes(option?.value);
            }

            if (row?.original?.column.toLowerCase().includes('date')) {
              return ['EQ', 'GT', 'GTE', 'LT', 'LTE', 'NE'].includes(option?.value);
            }

            return option;
          })}
          validators={{
            ...fieldCondition?.validators,
            ...{
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fieldCondition, value);
                },
              },
            },
          }}
        />
      </User.Capability>
    );
  };

  /**
   * renderCellValue
   *
   * Dynamically displays value input fields based on the field and condition selected.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCellValue = (args) => {
    const { row } = args;
    const { index } = row;

    const isFieldDisabled =
      !isUndefined(form?.formState?.errors?.[filterKey]?.[index]?.column) ||
      isEmpty(row?.original?.column) ||
      !isUndefined(form?.formState?.errors?.[filterKey]?.[index]?.condition) ||
      isEmpty(row?.original?.condition);

    return (
      <User.Capability
        alias="edit"
        attributes={permissions?.edit?.attributes}
        isAllowed={permissions?.edit?.isAllowed}
        resource={permissions?.edit?.resource}
      >
        {row?.original?.column === 'ACTION_PATH_ID' && (
          <SelectInputMulti
            {...fieldValue?.actionPath}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            onChange={() => {
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            validators={{
              ...fieldValue?.actionPath?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.actionPath, value);
                  },
                },
              },
            }}
          />
        )}

        {row?.original?.column === 'ACTION_STATUS' && (
          <SelectInput
            {...fieldValue?.status}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            onChange={() => {
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            validators={{
              ...fieldValue?.status?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.status, value);
                  },
                },
              },
            }}
          />
        )}

        {row?.original?.column === 'SPLIT_DATE' && (
          <SelectInput
            {...fieldValue?.splitDate}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            onChange={() => {
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            options={[{ label: 'Entity Actions Create Date', value: 'ENTITY_ACTIONS_CREATE_DATE' }]}
            validators={{
              ...fieldValue?.splitDate?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.splitDate, value);
                  },
                },
              },
            }}
          />
        )}

        {row?.original?.column === 'TAGS' && (
          <TagSelectionField
            form={form}
            handleChange={() => {
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            showLabel={false}
            showSelectAll={false}
            validators={{
              ...fieldValue?.tags?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.tags, value);
                  },
                },
              },
            }}
          />
        )}

        {row?.original?.column.toLowerCase().includes('count') && (
          <NumberInput
            {...fieldValue?.count}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            onChange={() => {
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            validators={{
              ...fieldValue?.count?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.count, value);
                  },
                },
              },
            }}
          />
        )}

        {row?.original?.column.toLowerCase().includes('date') && row?.original?.column !== 'SPLIT_DATE' && (
          <DateInput
            {...fieldValue?.date}
            isDisabled={isDisabled || isFieldDisabled}
            model={`${filterKey}[${index}][value]`}
            onChange={(dateValue) => {
              form?.setValue(`${filterKey}[${index}][value]`, dayjs(dateValue?.value).format('MM/DD/YYYY'), {
                shouldDirty: true,
              });
              validateField(index, 'condition');
              validateField(index, 'column');
              validateField(index, 'value');
            }}
            validators={{
              ...fieldValue?.date?.validators,
              ...{
                validate: {
                  customValidator: (value) => {
                    return fieldValidator(fieldValue?.date, value);
                  },
                },
              },
            }}
          />
        )}

        {!['ACTION_PATH_ID', 'ACTION_STATUS', 'TAGS'].includes(row?.original?.column) &&
          !row?.original?.column.toLowerCase().includes('date') &&
          !row?.original?.column.toLowerCase().includes('count') && (
            <TextInput
              {...fieldValue?.default}
              isDisabled={isDisabled || isFieldDisabled}
              model={`${filterKey}[${index}][value]`}
              onChange={() => {
                validateField(index, 'condition');
                validateField(index, 'column');
                validateField(index, 'value');
              }}
              validators={{
                ...fieldValue?.default?.validators,
                ...{
                  validate: {
                    customValidator: (value) => {
                      return fieldValidator(fieldValue?.default, value);
                    },
                  },
                },
              }}
            />
          )}
      </User.Capability>
    );
  };

  /**
   * renderCellActions
   *
   * Displays a button and icon to remove a field/row.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCellActions = (args) => {
    const { row, rows } = args;
    const { index } = row;

    return (
      <User.Capability
        alias="remove"
        attributes={permissions?.remove?.attributes}
        isAllowed={permissions?.remove?.isAllowed}
        resource={permissions?.remove?.resource}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          className={isDisabled ? 'removeField isDisabled-true' : 'removeField'}
          isDisabled={isDisabled}
          onClick={() => {
            if (rows.length === 1) {
              handleReset();
            } else {
              remove(index);
            }
          }}
          variant="custom"
        >
          <Flex alignItems="center">
            <div>
              <IconSymbol icon="cancel" variant="filled" />
              <IconSymbol icon="cancel" variant="outlined" />
            </div>
            <div>Remove</div>
          </Flex>
        </Link>
      </User.Capability>
    );
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns.map((item) => {
      const column = item;

      if (column.Header === 'Field') {
        column.Cell = renderCellField;
      }

      if (column.Header === 'Condition') {
        column.Cell = renderCellCondition;
      }

      if (column.Header === 'Value') {
        column.Cell = renderCellValue;
        column.Filters = filters;
      }

      if (column.accessor === 'actions') {
        column.Cell = renderCellActions;
      }

      return column;
    });
  }, [filters]);

  /**
   * reset
   *
   * This function is intended to be passed to the common table component. It will reset the form state fields
   *
   * @param values
   */
  const reset = useCallback(
    (values) => {
      let theValues = values;

      if (isEmpty(values)) {
        theValues = filters;
      }

      form?.resetField(filterKey, { defaultValue: theValues });
      theValues.forEach((formFields, index) => {
        Object.keys(formFields).forEach((column) => {
          form.setValue(`${filterKey}[${index}][${column}]`, formFields[column]);
        });
      });
    },
    [filters]
  );

  return (
    <ErrorHandler location="src/entities/Criteria/ui/components/Filters/components/Table/Table.jsx">
      <Styles>
        <TableComponent
          {...{
            columns,
            configuration: merge({}, configuration, {
              data: filters,
              enableGroupBy: false,
              isLoading,
              reorderRows: false,
            }),
            dataKey,
            noDataMessage: (
              <NotFound
                button={
                  /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                  <Link
                    className={isDisabled ? 'isDisabled-true' : ''}
                    css={{ height: '100%' }}
                    id="addField"
                    isDisabled={isDisabled}
                    onClick={() => {
                      append({ column: '', condition: '', value: '' });
                    }}
                    variant="custom"
                  >
                    <Flex alignItems="center" css={{ height: '100%' }}>
                      <div>
                        <IconSymbol icon="add_circle" variant="filled" />
                        <IconSymbol icon="add_circle" variant="outlined" />
                      </div>
                      <div>Add Filter</div>
                    </Flex>
                  </Link>
                }
                icon="filter_list_off"
                message="Get started by adding a new criteria filter."
                title="No Filters Added"
              />
            ),
            reset,
            rows: filters,
          }}
        />
      </Styles>
    </ErrorHandler>
  );
}
